import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminSession } from "../../app/providers";
import { pathForAdminEntityScreen, pathForReconciliationIssue } from "../../app/routes";
import type { Schemas } from "../workspace/api.generated";
import { ReconciliationListView } from "./components/ReconciliationListView";
import type { ReconciliationListTab } from "./components/ReconciliationListView";
import {
  useReconciliationIssue,
  useReconciliationIssues,
  useReconciliationMutation,
  useReconciliationProviders,
  useReconciliationRuns,
} from "./queries";
import { parseIssueStatus, parseReconciliationId } from "./schema";
import type { IssueStatusFilter } from "./schema";

function listParams(tab: ReconciliationListTab, issueStatus: IssueStatusFilter) {
  const params = new URLSearchParams({ tab });
  if (tab === "issues" && issueStatus) params.set("issueStatus", issueStatus);
  return params;
}

function queryState(query: { isPending: boolean; isError: boolean }) {
  return query.isPending ? "loading" as const : query.isError ? "error" as const : "ready" as const;
}

export function ReconciliationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useAdminSession();
  const authorities = session.authSession?.authorities ?? [];
  const canManage = authorities.includes("PROVIDER_MANAGE");
  const tab: ReconciliationListTab = searchParams.get("tab") === "issues" ? "issues" : "runs";
  const issueStatus = parseIssueStatus(searchParams.get("issueStatus"));
  const legacyRunId = parseReconciliationId(searchParams.get("runId"));
  const legacyIssueId = parseReconciliationId(searchParams.get("issueId"));
  const legacyIssue = useReconciliationIssue(legacyIssueId);
  const runs = useReconciliationRuns(tab === "runs");
  const issues = useReconciliationIssues(issueStatus, tab === "issues");
  const providers = useReconciliationProviders(canManage);
  const mutation = useReconciliationMutation();
  const [notice, setNotice] = useState("");
  const navigation = useAdminNavigation({
    contextKey: "reconciliations",
    defaultListPath: "/operations/reconciliations?tab=runs",
    originLabel: "대사 관리",
  });
  const restorationNotice = useRestoreAdminListViewport("reconciliations", tab === "runs" ? Boolean(runs.data) : Boolean(issues.data));

  useEffect(() => {
    if (legacyIssueId) {
      if (!legacyIssue.data) return;
      const runId = legacyRunId ?? legacyIssue.data.reconciliationRunId;
      navigate(pathForReconciliationIssue(runId, legacyIssueId), { replace: true });
      return;
    }
    if (legacyRunId) {
      const suffix = issueStatus ? `?issueStatus=${issueStatus}&page=0&size=20` : "?page=0&size=20";
      navigate(`${pathForAdminEntityScreen("30", legacyRunId)}${suffix}`, { replace: true });
      return;
    }
    const canonical = listParams(tab, issueStatus);
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true });
  }, [issueStatus, legacyIssue.data, legacyIssueId, legacyRunId, navigate, searchParams, setSearchParams, tab]);

  const changeList = (nextTab: ReconciliationListTab, nextStatus: IssueStatusFilter = "") => {
    setNotice("");
    setSearchParams(listParams(nextTab, nextStatus));
    navigation.focusListResults();
  };

  const openRun = (run: Schemas["ReconciliationRunView"]) => {
    navigation.openDetail(pathForAdminEntityScreen("30", run.id), `reconciliation-run:${run.id}:detail`);
  };

  const openIssue = (issue: Schemas["ReconciliationIssueView"]) => {
    navigation.openDetail(
      pathForReconciliationIssue(issue.reconciliationRunId, issue.id),
      `reconciliation-issue:${issue.id}:detail`,
    );
  };

  return <ReconciliationListView
    tab={tab}
    issueStatus={issueStatus}
    runs={runs.data}
    runsState={queryState(runs)}
    issues={issues.data}
    issuesState={queryState(issues)}
    providers={providers.data}
    providersState={canManage ? queryState(providers) : "idle"}
    canManage={canManage}
    notice={[notice, restorationNotice].filter(Boolean).join(" ")}
    runHref={(runId) => pathForAdminEntityScreen("30", runId)}
    issueHref={pathForReconciliationIssue}
    onTabChange={(nextTab) => changeList(nextTab)}
    onIssueStatusChange={(nextStatus) => changeList("issues", nextStatus)}
    onOpenRun={openRun}
    onOpenIssue={openIssue}
    onRetryRuns={() => { void runs.refetch(); }}
    onRetryIssues={() => { void issues.refetch(); }}
    onRetryProviders={() => { void providers.refetch(); }}
    onMutation={(input) => mutation.mutateAsync(input)}
    onRunComplete={(run) => {
      setNotice(`${run.runNo} 실행이 ${run.status} 상태로 완료되었습니다.`);
      navigation.openDetail(pathForAdminEntityScreen("30", run.id));
    }}
  />;
}
